import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Palette, Mail, MapPin, CreditCard, FileText, Upload, X, Server, Info, Loader2, CheckCircle2, XCircle, Clock, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import { agencyConfigApi, currencyMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { TimezoneSelector } from '@/components/settings/TimezoneSelector'
import type { UpdateAgencyConfigurationRequest } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'
import { isValidEmail, sanitizePhoneNumber } from '@/lib/utils'

// Blueprint Desk Default Color Palette
const DEFAULT_COLORS = {
  background_color: '#F7F7F7',           // Light gray - page background
  primary_color: '#001D39',              // Deep navy - buttons, links, focus rings, page headings
  primary_foreground_color: '#FFFFFF',   // White - text on primary buttons
  secondary_color: '#7BBDE8',            // Light blue - secondary buttons, muted backgrounds
  secondary_foreground_color: '#001D39', // Deep navy - text on secondary buttons
  accent_color: '#438CC4',               // Medium blue - hover states, highlights
  text_color: '#001D39',                 // Deep navy - main text color
  hamburger_menu_color: '#001D39',       // Deep navy - sidebar background
  sidebar_text_color: '#FFFFFF',         // White - sidebar text
}

export default function AgencyConfigurationPage() {
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [formData, setFormData] = useState<UpdateAgencyConfigurationRequest>({
    background_color: DEFAULT_COLORS.background_color,
    primary_color: DEFAULT_COLORS.primary_color,
    primary_foreground_color: DEFAULT_COLORS.primary_foreground_color,
    secondary_color: DEFAULT_COLORS.secondary_color,
    secondary_foreground_color: DEFAULT_COLORS.secondary_foreground_color,
    accent_color: DEFAULT_COLORS.accent_color,
    text_color: DEFAULT_COLORS.text_color,
    hamburger_menu_color: DEFAULT_COLORS.hamburger_menu_color,
    sidebar_text_color: DEFAULT_COLORS.sidebar_text_color,
    timezone: 'UTC',
    base_currency: 'USD',
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isTestingSmtp, setIsTestingSmtp] = useState(false)
  const [smtpTestResult, setSmtpTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }

    // Validate file size (max 500KB for base64 storage)
    if (file.size > 500 * 1024) {
      toast.error('Logo file must be less than 500KB')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const base64 = e.target?.result as string
      setFormData({ ...formData, logo_url: base64 })
    }
    reader.onerror = () => {
      toast.error('Failed to read file')
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveLogo = () => {
    setFormData({ ...formData, logo_url: '' })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const { data: config, isLoading } = useQuery({
    queryKey: ['agencyConfig'],
    queryFn: () => agencyConfigApi.get(),
  })

  const { data: currencies = [] } = useQuery({
    queryKey: ['currencies', true],
    queryFn: () => currencyMasterApi.getAll(true),
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAgencyConfigurationRequest) => agencyConfigApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agencyConfig'] })
      setIsSaving(false)
      toast.success('Configuration saved successfully')
    },
    onError: (error: any) => {
      setIsSaving(false)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save configuration'
      toast.error(errorMessage)
    },
  })

  useEffect(() => {
    if (config) {
      setFormData({
        agency_name: config.agency_name || '',
        logo_url: config.logo_url || '',
        background_color: config.background_color || DEFAULT_COLORS.background_color,
        primary_color: config.primary_color || DEFAULT_COLORS.primary_color,
        primary_foreground_color: config.primary_foreground_color || DEFAULT_COLORS.primary_foreground_color,
        secondary_color: config.secondary_color || DEFAULT_COLORS.secondary_color,
        secondary_foreground_color: config.secondary_foreground_color || DEFAULT_COLORS.secondary_foreground_color,
        accent_color: config.accent_color || DEFAULT_COLORS.accent_color,
        text_color: config.text_color || DEFAULT_COLORS.text_color,
        hamburger_menu_color: config.hamburger_menu_color || DEFAULT_COLORS.hamburger_menu_color,
        sidebar_text_color: config.sidebar_text_color || DEFAULT_COLORS.sidebar_text_color,
        email: config.email || '',
        phone: config.phone || '',
        website: config.website || '',
        address_line1: config.address_line1 || '',
        address_line2: config.address_line2 || '',
        city: config.city || '',
        state: config.state || '',
        postal_code: config.postal_code || '',
        country: config.country || '',
        bank_name: config.bank_name || '',
        account_name: config.account_name || '',
        account_number: config.account_number || '',
        routing_number: config.routing_number || '',
        swift_code: config.swift_code || '',
        iban: config.iban || '',
        payment_instructions: config.payment_instructions || '',
        tax_id: config.tax_id || '',
        tax_registration_number: config.tax_registration_number || '',
        // SMTP Settings
        smtp_enabled: config.smtp_enabled || false,
        smtp_host: config.smtp_host || '',
        smtp_port: config.smtp_port || 587,
        smtp_username: config.smtp_username || '',
        smtp_password: config.smtp_password || '',
        smtp_from_email: config.smtp_from_email || '',
        smtp_from_name: config.smtp_from_name || '',
        smtp_encryption: config.smtp_encryption || 'TLS',
        // Timezone
        timezone: config.timezone || 'UTC',
        // Currency
        base_currency: config.base_currency || 'USD',
      })
    }
  }, [config])

  const handleSave = () => {
    // Validate email fields
    if (formData.email && formData.email.trim() && !isValidEmail(formData.email)) {
      toast.error('Please enter a valid contact email address')
      return
    }
    if (formData.smtp_from_email && formData.smtp_from_email.trim() && !isValidEmail(formData.smtp_from_email)) {
      toast.error('Please enter a valid SMTP from email address')
      return
    }
    setIsSaving(true)
    updateMutation.mutate(formData)
  }

  const handleResetColors = () => {
    setFormData({
      ...formData,
      background_color: DEFAULT_COLORS.background_color,
      primary_color: DEFAULT_COLORS.primary_color,
      primary_foreground_color: DEFAULT_COLORS.primary_foreground_color,
      secondary_color: DEFAULT_COLORS.secondary_color,
      secondary_foreground_color: DEFAULT_COLORS.secondary_foreground_color,
      accent_color: DEFAULT_COLORS.accent_color,
      text_color: DEFAULT_COLORS.text_color,
      hamburger_menu_color: DEFAULT_COLORS.hamburger_menu_color,
      sidebar_text_color: DEFAULT_COLORS.sidebar_text_color,
    })
    toast.success('Colors reset to Blueprint Desk defaults. Click "Save Changes" to apply.')
  }

  const handleTestSmtp = async () => {
    if (!formData.smtp_host || !formData.smtp_port) {
      toast.error('Please enter SMTP host and port')
      return
    }

    setIsTestingSmtp(true)
    setSmtpTestResult(null)

    try {
      const result = await agencyConfigApi.testSmtp({
        smtp_host: formData.smtp_host,
        smtp_port: formData.smtp_port,
        smtp_username: formData.smtp_username,
        smtp_password: formData.smtp_password,
        smtp_encryption: formData.smtp_encryption,
      })

      if (result.success) {
        setSmtpTestResult({ success: true, message: result.message || 'Connection successful!' })
        toast.success('SMTP connection successful!')
      } else {
        setSmtpTestResult({ success: false, message: result.error || 'Connection failed' })
        toast.error('SMTP connection failed: ' + (result.error || 'Unknown error'))
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Connection failed'
      setSmtpTestResult({ success: false, message: errorMessage })
      toast.error('SMTP connection failed: ' + errorMessage)
    } finally {
      setIsTestingSmtp(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading configuration...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Agency Config</h1>
          <p className="text-muted-foreground">
            Configure your agency's branding, contact details, and payment information
          </p>
        </div>
        {canAddEdit('AGENCY_CONFIG') && (
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="branding" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Contact
          </TabsTrigger>
          <TabsTrigger value="address" className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Address
          </TabsTrigger>
          <TabsTrigger value="payment" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Payment
          </TabsTrigger>
          <TabsTrigger value="tax" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Tax Info
          </TabsTrigger>
          <TabsTrigger value="smtp" className="flex items-center gap-2">
            <Server className="h-4 w-4" />
            Email/SMTP
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                General Settings
              </CardTitle>
              <CardDescription>
                Configure general agency settings including name, logo, and timezone
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="agency_name">Agency Name</Label>
                  <Input
                    id="agency_name"
                    value={formData.agency_name || ''}
                    onChange={(e) => setFormData({ ...formData, agency_name: e.target.value })}
                    placeholder="Enter your Agency Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Agency Logo</Label>
                  <div className="flex flex-col gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-1"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload Logo
                      </Button>
                      {formData.logo_url && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleRemoveLogo}
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Recommended: PNG or SVG, max 500KB
                    </p>
                  </div>
                </div>
              </div>

              {/* Logo Preview */}
              {formData.logo_url && (
                <div className="space-y-2">
                  <Label>Logo Preview</Label>
                  <div className="border rounded-lg p-4 bg-muted/50 inline-block">
                    <img
                      src={formData.logo_url}
                      alt="Agency Logo"
                      className="max-h-20 max-w-[200px] object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="timezone">Agency Timezone</Label>
                <TimezoneSelector
                  value={formData.timezone || 'UTC'}
                  onChange={(timezone) => setFormData({ ...formData, timezone })}
                />
                <p className="text-sm text-muted-foreground">
                  All times in the application will be displayed in this timezone. Time entries, reports, and calendars will use this timezone for display.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="base_currency">Base Currency (Reporting Currency)</Label>
                <Select
                  value={formData.base_currency || 'USD'}
                  onValueChange={(value) => setFormData({ ...formData, base_currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select base currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((currency) => (
                      <SelectItem key={currency.currency_id} value={currency.currency_code}>
                        {currency.currency_code} - {currency.currency_name} ({currency.currency_symbol})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  All financial reports and dashboard metrics will be converted to this currency. This is used for multi-currency expense and invoice tracking.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Color Theme
              </CardTitle>
              <CardDescription>
                Customize your agency's color scheme
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleResetColors}
                  className="flex items-center gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset to Default
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Background Color */}
                  <div className="space-y-2 p-4 border rounded-lg">
                    <Label htmlFor="background_color" className="text-sm font-medium">
                      Background Color
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Main page background color
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="background_color"
                        type="color"
                        value={formData.background_color || DEFAULT_COLORS.background_color}
                        onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.background_color || DEFAULT_COLORS.background_color}
                        onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                        placeholder={DEFAULT_COLORS.background_color}
                      />
                    </div>
                  </div>

                  {/* Sidebar Color */}
                  <div className="space-y-2 p-4 border rounded-lg">
                    <Label htmlFor="hamburger_menu_color" className="text-sm font-medium">
                      Sidebar Color
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Navigation sidebar background color
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="hamburger_menu_color"
                        type="color"
                        value={formData.hamburger_menu_color || DEFAULT_COLORS.hamburger_menu_color}
                        onChange={(e) => setFormData({ ...formData, hamburger_menu_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.hamburger_menu_color || DEFAULT_COLORS.hamburger_menu_color}
                        onChange={(e) => setFormData({ ...formData, hamburger_menu_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                        placeholder={DEFAULT_COLORS.hamburger_menu_color}
                      />
                    </div>
                  </div>

                  {/* Sidebar Text Color */}
                  <div className="space-y-2 p-4 border rounded-lg">
                    <Label htmlFor="sidebar_text_color" className="text-sm font-medium">
                      Sidebar Text Color
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Text color for sidebar menu items
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="sidebar_text_color"
                        type="color"
                        value={formData.sidebar_text_color || DEFAULT_COLORS.sidebar_text_color}
                        onChange={(e) => setFormData({ ...formData, sidebar_text_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.sidebar_text_color || DEFAULT_COLORS.sidebar_text_color}
                        onChange={(e) => setFormData({ ...formData, sidebar_text_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                        placeholder={DEFAULT_COLORS.sidebar_text_color}
                      />
                    </div>
                  </div>

                  {/* Primary Color */}
                  <div className="space-y-2 p-4 border rounded-lg">
                    <Label htmlFor="primary_color" className="text-sm font-medium">
                      Primary Color
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Page headings, main buttons, links, focus rings
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="primary_color"
                        type="color"
                        value={formData.primary_color || DEFAULT_COLORS.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.primary_color || DEFAULT_COLORS.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                        placeholder={DEFAULT_COLORS.primary_color}
                      />
                    </div>
                  </div>

                  {/* Text on Primary Button */}
                  <div className="space-y-2 p-4 border rounded-lg">
                    <Label htmlFor="primary_foreground_color" className="text-sm font-medium">
                      Text on Primary Button
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Text color on primary buttons
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="primary_foreground_color"
                        type="color"
                        value={formData.primary_foreground_color || DEFAULT_COLORS.primary_foreground_color}
                        onChange={(e) => setFormData({ ...formData, primary_foreground_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.primary_foreground_color || DEFAULT_COLORS.primary_foreground_color}
                        onChange={(e) => setFormData({ ...formData, primary_foreground_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                        placeholder={DEFAULT_COLORS.primary_foreground_color}
                      />
                    </div>
                  </div>

                  {/* Secondary Color */}
                  <div className="space-y-2 p-4 border rounded-lg">
                    <Label htmlFor="secondary_color" className="text-sm font-medium">
                      Secondary Color
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Secondary buttons, muted backgrounds
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="secondary_color"
                        type="color"
                        value={formData.secondary_color || DEFAULT_COLORS.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.secondary_color || DEFAULT_COLORS.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                        placeholder={DEFAULT_COLORS.secondary_color}
                      />
                    </div>
                  </div>

                  {/* Text on Secondary Button */}
                  <div className="space-y-2 p-4 border rounded-lg">
                    <Label htmlFor="secondary_foreground_color" className="text-sm font-medium">
                      Text on Secondary Button
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Text color on secondary buttons
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="secondary_foreground_color"
                        type="color"
                        value={formData.secondary_foreground_color || DEFAULT_COLORS.secondary_foreground_color}
                        onChange={(e) => setFormData({ ...formData, secondary_foreground_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.secondary_foreground_color || DEFAULT_COLORS.secondary_foreground_color}
                        onChange={(e) => setFormData({ ...formData, secondary_foreground_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                        placeholder={DEFAULT_COLORS.secondary_foreground_color}
                      />
                    </div>
                  </div>

                  {/* Accent Color */}
                  <div className="space-y-2 p-4 border rounded-lg">
                    <Label htmlFor="accent_color" className="text-sm font-medium">
                      Accent Color
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Hover states, highlights, interactive elements
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="accent_color"
                        type="color"
                        value={formData.accent_color || DEFAULT_COLORS.accent_color}
                        onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.accent_color || DEFAULT_COLORS.accent_color}
                        onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                        placeholder={DEFAULT_COLORS.accent_color}
                      />
                    </div>
                  </div>

                  {/* Text Color */}
                  <div className="space-y-2 p-4 border rounded-lg">
                    <Label htmlFor="text_color" className="text-sm font-medium">
                      Text Color
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Main text color throughout the app
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Input
                        id="text_color"
                        type="color"
                        value={formData.text_color || DEFAULT_COLORS.text_color}
                        onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={formData.text_color || DEFAULT_COLORS.text_color}
                        onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                        className="flex-1 font-mono text-sm"
                        placeholder={DEFAULT_COLORS.text_color}
                      />
                    </div>
                  </div>
              </div>

              {/* Color Preview */}
              <div className="mt-6 p-4 border rounded-lg">
                  <Label className="text-sm text-muted-foreground mb-3 block">Live Preview</Label>
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Sidebar Preview */}
                    <div
                      className="w-full md:w-48 p-4 rounded-lg"
                      style={{ backgroundColor: formData.hamburger_menu_color || DEFAULT_COLORS.hamburger_menu_color }}
                    >
                      <p
                        className="text-sm font-medium mb-2"
                        style={{ color: formData.sidebar_text_color || DEFAULT_COLORS.sidebar_text_color }}
                      >
                        Sidebar
                      </p>
                      <div className="space-y-1">
                        <div
                          className="text-xs"
                          style={{ color: formData.sidebar_text_color || DEFAULT_COLORS.sidebar_text_color, opacity: 0.8 }}
                        >
                          Dashboard
                        </div>
                        <div
                          className="text-xs"
                          style={{ color: formData.sidebar_text_color || DEFAULT_COLORS.sidebar_text_color, opacity: 0.8 }}
                        >
                          Projects
                        </div>
                        <div
                          className="text-xs"
                          style={{ color: formData.sidebar_text_color || DEFAULT_COLORS.sidebar_text_color, opacity: 0.8 }}
                        >
                          Clients
                        </div>
                      </div>
                    </div>

                    {/* Main Content Preview */}
                    <div
                      className="flex-1 p-4 rounded-lg border"
                      style={{ backgroundColor: formData.background_color || DEFAULT_COLORS.background_color }}
                    >
                      <h3
                        className="text-lg font-bold mb-2"
                        style={{ color: formData.primary_color || DEFAULT_COLORS.primary_color }}
                      >
                        {formData.agency_name || 'Your Agency Name'}
                      </h3>
                      <p
                        className="text-sm mb-3"
                        style={{ color: formData.text_color || DEFAULT_COLORS.text_color }}
                      >
                        This is how your content will look with the selected colors.
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          className="px-3 py-1.5 rounded text-sm"
                          style={{
                            backgroundColor: formData.primary_color || DEFAULT_COLORS.primary_color,
                            color: formData.primary_foreground_color || DEFAULT_COLORS.primary_foreground_color
                          }}
                        >
                          Primary Button
                        </button>
                        <button
                          className="px-3 py-1.5 rounded text-sm"
                          style={{
                            backgroundColor: `${formData.secondary_color || DEFAULT_COLORS.secondary_color}30`,
                            color: formData.secondary_foreground_color || DEFAULT_COLORS.secondary_foreground_color
                          }}
                        >
                          Secondary
                        </button>
                        <a
                          href="#"
                          className="px-3 py-1.5 text-sm underline"
                          style={{ color: formData.accent_color || DEFAULT_COLORS.accent_color }}
                          onClick={(e) => e.preventDefault()}
                        >
                          Accent Link
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Tab */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Contact Details
              </CardTitle>
              <CardDescription>
                Your agency's contact information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter your Email ID"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: sanitizePhoneNumber(e.target.value) })}
                    placeholder="Enter your Phone Number"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website || ''}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="Enter your Website"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Address Tab */}
        <TabsContent value="address">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Business Address
              </CardTitle>
              <CardDescription>
                Your agency's physical address
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address_line1">Address Line 1</Label>
                <Input
                  id="address_line1"
                  value={formData.address_line1 || ''}
                  onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  placeholder="Enter your Address Line 1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address_line2">Address Line 2</Label>
                <Input
                  id="address_line2"
                  value={formData.address_line2 || ''}
                  onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                  placeholder="Enter your Address Line 2"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Enter your City"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State/Province</Label>
                  <Input
                    id="state"
                    value={formData.state || ''}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="Enter your State/Province"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code || ''}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="Enter your Postal Code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country || ''}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="Enter your Country"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Tab */}
        <TabsContent value="payment">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Payment Details
              </CardTitle>
              <CardDescription>
                Bank account and payment information for invoices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={formData.bank_name || ''}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    placeholder="Enter your Bank Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account_name">Account Name</Label>
                  <Input
                    id="account_name"
                    value={formData.account_name || ''}
                    onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                    placeholder="Enter your Account Name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="account_number">Account Number</Label>
                  <Input
                    id="account_number"
                    value={formData.account_number || ''}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    placeholder="Enter your Account Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="routing_number">Routing Number</Label>
                  <Input
                    id="routing_number"
                    value={formData.routing_number || ''}
                    onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
                    placeholder="Enter your Routing Number"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="swift_code">SWIFT/BIC Code</Label>
                  <Input
                    id="swift_code"
                    value={formData.swift_code || ''}
                    onChange={(e) => setFormData({ ...formData, swift_code: e.target.value })}
                    placeholder="Enter your SWIFT/BIC Code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="iban">IBAN</Label>
                  <Input
                    id="iban"
                    value={formData.iban || ''}
                    onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                    placeholder="Enter your IBAN"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_instructions">Payment Instructions</Label>
                <Textarea
                  id="payment_instructions"
                  value={formData.payment_instructions || ''}
                  onChange={(e) => setFormData({ ...formData, payment_instructions: e.target.value })}
                  placeholder="Enter your Payment Instructions"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Info Tab */}
        <TabsContent value="tax">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Tax Information
              </CardTitle>
              <CardDescription>
                Tax identification and registration details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tax_id">Tax ID / EIN</Label>
                  <Input
                    id="tax_id"
                    value={formData.tax_id || ''}
                    onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                    placeholder="Enter your Tax ID / EIN"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tax_registration_number">Tax Registration Number</Label>
                  <Input
                    id="tax_registration_number"
                    value={formData.tax_registration_number || ''}
                    onChange={(e) => setFormData({ ...formData, tax_registration_number: e.target.value })}
                    placeholder="Enter your Tax Registration Number"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP/Email Tab */}
        <TabsContent value="smtp">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  SMTP Configuration
                </CardTitle>
                <CardDescription>
                  Configure your email server settings to send invoices directly to clients
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable SMTP Toggle */}
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="smtp_enabled" className="text-base">Enable SMTP</Label>
                    <p className="text-sm text-muted-foreground">
                      Turn on to send invoices via email
                    </p>
                  </div>
                  <Switch
                    id="smtp_enabled"
                    checked={formData.smtp_enabled || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, smtp_enabled: checked })}
                  />
                </div>

                {/* SMTP Server Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp_host">SMTP Host</Label>
                    <Input
                      id="smtp_host"
                      value={formData.smtp_host || ''}
                      onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                      placeholder="Enter your SMTP Host"
                      disabled={!formData.smtp_enabled}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_port">SMTP Port</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      value={formData.smtp_port || 587}
                      onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) || 587 })}
                      placeholder="Enter your SMTP Port"
                      disabled={!formData.smtp_enabled}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp_username">Username</Label>
                    <Input
                      id="smtp_username"
                      value={formData.smtp_username || ''}
                      onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                      placeholder="Enter your SMTP Username"
                      disabled={!formData.smtp_enabled}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_password">Password / App Password</Label>
                    <Input
                      id="smtp_password"
                      type="password"
                      value={formData.smtp_password || ''}
                      onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                      placeholder="Enter your SMTP Password"
                      disabled={!formData.smtp_enabled}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp_encryption">Encryption</Label>
                  <Select
                    value={formData.smtp_encryption || 'TLS'}
                    onValueChange={(value: 'TLS' | 'SSL' | 'NONE') => setFormData({ ...formData, smtp_encryption: value })}
                    disabled={!formData.smtp_enabled}
                  >
                    <SelectTrigger id="smtp_encryption">
                      <SelectValue placeholder="Select encryption type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TLS">TLS (Recommended - Port 587)</SelectItem>
                      <SelectItem value="SSL">SSL (Port 465)</SelectItem>
                      <SelectItem value="NONE">None (Port 25 - Not Recommended)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Sender Information */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium mb-4">Sender Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp_from_email">From Email</Label>
                      <Input
                        id="smtp_from_email"
                        type="email"
                        value={formData.smtp_from_email || ''}
                        onChange={(e) => setFormData({ ...formData, smtp_from_email: e.target.value })}
                        placeholder="Enter your From Email"
                        disabled={!formData.smtp_enabled}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp_from_name">From Name</Label>
                      <Input
                        id="smtp_from_name"
                        value={formData.smtp_from_name || ''}
                        onChange={(e) => setFormData({ ...formData, smtp_from_name: e.target.value })}
                        placeholder="Enter your From Name"
                        disabled={!formData.smtp_enabled}
                      />
                    </div>
                  </div>
                </div>

                {/* Test Connection */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium">Test Connection</h4>
                      <p className="text-sm text-muted-foreground">
                        Verify your SMTP settings are correct
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleTestSmtp}
                      disabled={isTestingSmtp || !formData.smtp_host}
                    >
                      {isTestingSmtp ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Test Connection
                        </>
                      )}
                    </Button>
                  </div>
                  {smtpTestResult && (
                    <Alert className={`mt-4 ${smtpTestResult.success ? 'border-green-500' : 'border-red-500'}`}>
                      {smtpTestResult.success ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <AlertTitle>{smtpTestResult.success ? 'Success' : 'Failed'}</AlertTitle>
                      <AlertDescription>{smtpTestResult.message}</AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* SMTP Provider Setup Guide */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  SMTP Provider Setup Guide
                </CardTitle>
                <CardDescription>
                  Quick setup instructions for popular email providers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Gmail */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-red-100 flex items-center justify-center">
                        <span className="text-red-600 font-bold text-sm">G</span>
                      </div>
                      <span className="font-medium">Gmail / Google Workspace</span>
                    </div>
                    <span className="text-muted-foreground text-sm">Click to expand</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 p-4 rounded-lg bg-muted/30 space-y-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><strong>SMTP Host:</strong> smtp.gmail.com</div>
                      <div><strong>Port:</strong> 587 (TLS) or 465 (SSL)</div>
                      <div><strong>Username:</strong> your-email@gmail.com</div>
                      <div><strong>Encryption:</strong> TLS (recommended)</div>
                    </div>
                    <Alert className="mt-3">
                      <Info className="h-4 w-4" />
                      <AlertTitle>Important: Use App Password</AlertTitle>
                      <AlertDescription>
                        Gmail requires an App Password instead of your regular password.
                        Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline text-primary">Google Account → Security → App Passwords</a> to generate one.
                        You must have 2-Step Verification enabled.
                      </AlertDescription>
                    </Alert>
                  </CollapsibleContent>
                </Collapsible>

                {/* Yahoo */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 font-bold text-sm">Y</span>
                      </div>
                      <span className="font-medium">Yahoo Mail</span>
                    </div>
                    <span className="text-muted-foreground text-sm">Click to expand</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 p-4 rounded-lg bg-muted/30 space-y-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><strong>SMTP Host:</strong> smtp.mail.yahoo.com</div>
                      <div><strong>Port:</strong> 587 (TLS) or 465 (SSL)</div>
                      <div><strong>Username:</strong> your-email@yahoo.com</div>
                      <div><strong>Encryption:</strong> TLS or SSL</div>
                    </div>
                    <Alert className="mt-3">
                      <Info className="h-4 w-4" />
                      <AlertTitle>Important: Generate App Password</AlertTitle>
                      <AlertDescription>
                        Yahoo requires an App Password. Go to <a href="https://login.yahoo.com/account/security" target="_blank" rel="noopener noreferrer" className="underline text-primary">Yahoo Account → Security → Generate App Password</a>.
                        Select "Other App" and use the generated password.
                      </AlertDescription>
                    </Alert>
                  </CollapsibleContent>
                </Collapsible>

                {/* Outlook/Hotmail */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-bold text-sm">O</span>
                      </div>
                      <span className="font-medium">Outlook / Hotmail / Microsoft 365</span>
                    </div>
                    <span className="text-muted-foreground text-sm">Click to expand</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 p-4 rounded-lg bg-muted/30 space-y-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><strong>SMTP Host:</strong> smtp.office365.com</div>
                      <div><strong>Port:</strong> 587</div>
                      <div><strong>Username:</strong> your-email@outlook.com</div>
                      <div><strong>Encryption:</strong> TLS (STARTTLS)</div>
                    </div>
                    <Alert className="mt-3">
                      <Info className="h-4 w-4" />
                      <AlertTitle>Note for Microsoft 365</AlertTitle>
                      <AlertDescription>
                        For Microsoft 365 business accounts, you may need to enable SMTP AUTH in the admin center.
                        Personal Outlook/Hotmail accounts may require an App Password if 2FA is enabled.
                      </AlertDescription>
                    </Alert>
                  </CollapsibleContent>
                </Collapsible>

                {/* Other Providers */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center">
                        <Server className="h-4 w-4 text-gray-600" />
                      </div>
                      <span className="font-medium">Other SMTP Providers</span>
                    </div>
                    <span className="text-muted-foreground text-sm">Click to expand</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 p-4 rounded-lg bg-muted/30 space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Common SMTP settings for other providers:
                    </p>
                    <div className="space-y-3 text-sm">
                      <div className="p-2 rounded bg-background">
                        <strong>SendGrid:</strong> smtp.sendgrid.net, Port 587, TLS
                      </div>
                      <div className="p-2 rounded bg-background">
                        <strong>Mailgun:</strong> smtp.mailgun.org, Port 587, TLS
                      </div>
                      <div className="p-2 rounded bg-background">
                        <strong>Amazon SES:</strong> email-smtp.[region].amazonaws.com, Port 587, TLS
                      </div>
                      <div className="p-2 rounded bg-background">
                        <strong>Zoho Mail:</strong> smtp.zoho.com, Port 587 (TLS) or 465 (SSL)
                      </div>
                    </div>
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertTitle>Tip</AlertTitle>
                      <AlertDescription>
                        For production use, consider using a dedicated email service like SendGrid, Mailgun, or Amazon SES
                        for better deliverability and tracking.
                      </AlertDescription>
                    </Alert>
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

